import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $patchStyleText } from "@lexical/selection";
import FontDownload from "@mui/icons-material/FontDownload";
import { IconButton, Tooltip } from "@mui/material";
import { $getSelection, $isRangeSelection } from "lexical";
import { type FC, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../generic/components/colour-picker/colour-picker";
import { floatingToolbarStyles } from "./floating-toolbar-styles";
import type { ToolbarProps } from "./props";

export const FontBackgroundColourSelect: FC<ToolbarProps & { colour: string | null }> = ({
  disabled,
  colour,
  mode,
}) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  const onChange = useCallback(
    (newColour: string, skipAddingToHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if ($isRangeSelection(selection))
            $patchStyleText(selection, { "background-color": newColour });
        },
        skipAddingToHistoryStack ? { tag: "historic" } : {},
      );
    },
    [editor],
  );

  return (
    <>
      <Tooltip title={t("Highlight Color")}>
        <span>
          <IconButton
            data-analytics-id="lexical-toolbar-highlight"
            disabled={disabled}
            data-testid="toolbar-button-highlight-color"
            aria-label="Highlight Color"
            tabIndex={-1}
            sx={{
              ...floatingToolbarStyles[mode].button,
              backgroundColor: colour ? colour : "background.neutral",
            }}
            // eslint-disable-next-line react-hooks/refs
            onClick={popover.handleToggle}
            // eslint-disable-next-line react-hooks/refs
            ref={popover.anchorRef}
          >
            <FontDownload sx={floatingToolbarStyles[mode].formatIcon} />
          </IconButton>
        </span>
      </Tooltip>
      <ColourPicker
        // eslint-disable-next-line react-hooks/refs
        popover={popover}
        colour={colour ?? "#ffffff"}
        onChange={onChange}
        onInput={onChange}
        inputRef={popoverInputRef}
      />
    </>
  );
};
